import { AIPrefillBadge } from '@components/AIPrefillBadge'
import { HelpText } from '@components/DataForm/styles'
import { useRequiredForBooleanRule } from '@helpers/formValidation'
import { boolToString } from '@helpers/strings'
import {
	EMPTY_PREFILL_PROPS,
	type PrefillableFieldProps,
} from '@hooks/usePrefillableFieldProps'
import {
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	Radio,
	RadioGroup,
	type RadioGroupProps,
	Stack,
} from '@mui/material'
import { type ReactNode, useCallback } from 'react'
import {
	type Control,
	Controller,
	type ControllerFieldState,
	type ControllerRenderProps,
	type FieldPath,
	type FieldValues,
	type PathValue,
	useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FocusVisibleRadio } from '../FocusVisible'

// TODO(Jonathan): Feel like that should be the default, but need to do a pass where its being used!
const HELPER_TEXT_SX = { margin: 0 }

interface BooleanFieldControllerProps<
	TFieldValues extends FieldValues,
> extends Omit<RadioGroupProps, 'onChange'> {
	name: FieldPath<TFieldValues>
	control?: Control<TFieldValues>
	label?: string
	isRequired?: boolean
	prefillProps?: PrefillableFieldProps
	onChange?: (value: boolean) => void
	helperText?: string
	labelGenerator?: (value: boolean) => string
	labelAdornment?: ReactNode
	defaultValue?: boolean
	externalError?: string
}

const RadioGroupWithExtraArgOnChange = <T,>({
	onChange,
	extraArgOnChange,
	...props
}: Omit<RadioGroupProps, 'onChange'> & {
	extraArgOnChange: T
	onChange: (e: React.ChangeEvent<HTMLInputElement>, extraArg: T) => void
}) => {
	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => onChange(e, extraArgOnChange),
		[onChange, extraArgOnChange]
	)
	return <RadioGroup {...props} onChange={handleChange} />
}

export const BooleanFieldController = <TFieldValues extends FieldValues>({
	name,
	control: controlProp,
	label,
	isRequired = false,
	// TODO(larryhudson): Replace isPrefilled with a labelAdornment prop
	// TODO(larryhudson): Drop onFieldChange and require the caller to wrap the onChange
	prefillProps: { isPrefilled, onFieldChange } = EMPTY_PREFILL_PROPS,
	onChange: onChangeCallback,
	helperText,
	labelGenerator,
	labelAdornment,
	defaultValue,
	externalError,
	...props
}: BooleanFieldControllerProps<TFieldValues>) => {
	const { t } = useTranslation()
	const formContext = useFormContext<TFieldValues>()
	const control = controlProp ?? formContext.control
	const requiredForBooleanRule = useRequiredForBooleanRule()

	const handleChange = useCallback(
		(
			e: React.ChangeEvent<HTMLInputElement>,
			onChange: (value: boolean) => void
		) => {
			const newValue = e.target.value === 'true'

			if (onFieldChange) {
				onFieldChange(newValue, onChange)
			} else {
				onChange(newValue)
			}

			if (onChangeCallback) {
				onChangeCallback(newValue)
			}
		},
		[onChangeCallback, onFieldChange]
	)

	const yesLabel = labelGenerator ? labelGenerator(true) : t('common.yes')
	const noLabel = labelGenerator ? labelGenerator(false) : t('common.no')

	const renderField = useCallback(
		({
			field: { ref, ...field },
			fieldState: { error },
		}: {
			field: ControllerRenderProps<TFieldValues, FieldPath<TFieldValues>>
			fieldState: ControllerFieldState
		}) => (
			<FormControl component="fieldset" error={!!error || !!externalError}>
				{(label || labelAdornment) && (
					<Stack mb={1}>
						<Stack direction="row" alignItems="center" spacing={1}>
							<FormLabel component="legend">
								{label}
								{isRequired && ' *'}
							</FormLabel>
							{isPrefilled && <AIPrefillBadge />}
							{labelAdornment}
						</Stack>
					</Stack>
				)}
				<RadioGroupWithExtraArgOnChange
					{...props}
					value={boolToString(field.value ?? defaultValue, '')}
					onChange={handleChange}
					extraArgOnChange={field.onChange}
					row
				>
					<FormControlLabel
						value="true"
						control={<FocusVisibleRadio ref={ref} />}
						label={yesLabel}
					/>
					<FormControlLabel value="false" control={<Radio />} label={noLabel} />
				</RadioGroupWithExtraArgOnChange>
				{helperText && <HelpText style={HELPER_TEXT_SX}>{helperText}</HelpText>}
				<FormHelperText error={!!error || !!externalError} sx={{ ml: 0 }}>
					{error?.type === 'required'
						? t('input.required')
						: (error?.message ?? externalError)}
				</FormHelperText>
			</FormControl>
		),
		[
			externalError,
			handleChange,
			helperText,
			isPrefilled,
			isRequired,
			label,
			labelAdornment,
			noLabel,
			props,
			t,
			yesLabel,
			defaultValue,
		]
	)
	return (
		<Controller
			name={name}
			control={control}
			rules={isRequired ? requiredForBooleanRule : undefined}
			defaultValue={
				defaultValue as PathValue<TFieldValues, FieldPath<TFieldValues>>
			}
			render={renderField}
		/>
	)
}
